import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import useGetChangeSets from '../../api/useGetChangeSets';
import { ChangeSet } from '../../types/ChangeSetTypes';
import { PageWrapper, PageContainer } from '../../components/common/PageWrapper.styles';
import { MainContent } from '../../components/common/Layout.styles';
import { PaginationButton } from '../../components/shared/Button.styles';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { formatDateTime } from '../../utils/dateFormatters';
import {
    ChangelogPage,
    FilterBar,
    FilterLabel,
    Pagination,
    BorderedContainer,
    TableScrollContainer,
    HeaderRow,
    ChangeRow,
    EditSelect,
    EditInput,
    HideOverflow,
    StatusBadge,
    Error,
    NoData,
} from './ChangeLogPage.styles';

// Use ChangeSet view model for UI display
type ChangeLogEntry = ChangeSet;

// Generate list of years from current year back to 2020
const generateYearOptions = (): number[] => {
    const currentYear = new Date().getFullYear();
    const startYear = 2020;
    const years: number[] = [];
    for (let year = currentYear; year >= startYear; year--) {
        years.push(year);
    }
    return years;
};

const ChangeLogPage = () => {
    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState<number>(currentYear);
    const [selectedType, setSelectedType] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [endDateFilter, setEndDateFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 100; // Increased from 25 to 100 for server-side pagination
    const navigate = useNavigate();

    const yearOptions = useMemo(() => generateYearOptions(), []);

    // API integration for change sets with pagination
    const { changeSets, totalCount, loading, error, getChangeSets } = useGetChangeSets();

    // Handle row click - navigate to detail page
    const handleRowClick = (id: number) => {
        navigate(`/changelog?changeLogId=${id}`);
    };

    // Fetch change sets on component mount or when pagination/year changes
    useEffect(() => {
        const skip = (currentPage - 1) * itemsPerPage;
        getChangeSets({ skip, take: itemsPerPage, sortBy: 'DateTimeUtc', sortOrder: 'desc', year: selectedYear });
    }, [getChangeSets, currentPage, itemsPerPage, selectedYear]);

    // Reset to page 1 when year changes
    const handleYearChange = (year: number) => {
        setSelectedYear(year);
        setCurrentPage(1);
    };

    // Use API data or fallback to empty array
    const allChangeLogEntries: ChangeLogEntry[] = Array.isArray(changeSets) ? changeSets : [];

    const types = ['Created', 'Deleted', 'Updated'];
    const categories = [
        'Unknown',
        'Product',
        'Procedure',
        'Safety Hazard',
        'Coating',
        'Draeger Tube',
        'Product Document',
        'Procedure Document',
        'Safety Hazard Document',
        'Coating Document',
        'Category Document',
    ];
    const statuses = ['Pending Review', 'Reviewed'];
    const users = Array.from(new Set(allChangeLogEntries.map((entry) => entry.userName)));

    // Client-side filtering (applied to current page data)
    // Note: For full filtering support, server-side filtering should be implemented
    const filteredEntries = useMemo(() => {
        return allChangeLogEntries.filter((entry) => {
            const matchesType = !selectedType || entry.typeName === selectedType;
            const matchesCategory = !selectedCategory || entry.categoryName === selectedCategory;
            const matchesUser = !selectedUser || entry.userName === selectedUser;
            const matchesStatus = !selectedStatus || entry.statusName === selectedStatus;

            // Filter by end date - show entries on or before the selected date
            let matchesDate = true;
            if (endDateFilter) {
                const filterDate = new Date(endDateFilter);
                filterDate.setHours(23, 59, 59, 999); // Include the entire selected day
                matchesDate = entry.date <= filterDate;
            }

            return matchesType && matchesCategory && matchesUser && matchesStatus && matchesDate;
        });
    }, [selectedType, selectedCategory, selectedUser, selectedStatus, endDateFilter, allChangeLogEntries]);

    // Use server-side pagination metadata
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const paginatedEntries = filteredEntries; // Already paginated from server

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        const end = Math.min(totalPages, start + maxVisible - 1);

        if (end - start < maxVisible - 1) {
            start = Math.max(1, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    return (
        <PageWrapper>
            <ChangelogPage as={PageContainer}>
                <MainContent>
                    {/* Error State */}
                    {error && <Error>Error loading change sets: {error}</Error>}

                    {/* Year Filter */}
                    <FilterBar>
                        <ul>
                            <li>
                                <FilterLabel htmlFor="yearSelect">Filter by year:</FilterLabel>
                                <EditSelect
                                    id="yearSelect"
                                    value={selectedYear}
                                    onChange={(e) => handleYearChange(parseInt(e.target.value, 10))}
                                    aria-label="Filter by year"
                                >
                                    {yearOptions.map((year) => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))}
                                </EditSelect>
                            </li>
                        </ul>
                    </FilterBar>

                    {/* Table */}
                    <BorderedContainer>
                        <HeaderRow>
                            <div className="changeType">
                                <EditSelect value={selectedType} onChange={(e) => setSelectedType(e.target.value)} aria-label="Filter by change type">
                                    <option value="">Type</option>
                                    {types.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </EditSelect>
                            </div>
                            <div className="dateTime">
                                <EditInput
                                    type="date"
                                    gradientBackground
                                    value={endDateFilter}
                                    onChange={(e) => setEndDateFilter(e.target.value)}
                                    aria-label="Filter by end date"
                                />
                            </div>
                            <div className="category">
                                <EditSelect
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    aria-label="Filter by category"
                                >
                                    <option value="">Category</option>
                                    {categories.map((category) => (
                                        <option key={category} value={category}>
                                            {category}
                                        </option>
                                    ))}
                                </EditSelect>
                            </div>
                            <div></div>
                            <div className="changedBy">
                                <EditSelect value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)} aria-label="Filter by user">
                                    <option value="">User</option>
                                    {users.map((user) => (
                                        <option key={user} value={user}>
                                            {user}
                                        </option>
                                    ))}
                                </EditSelect>
                            </div>
                            <div className="changeStatus">
                                <EditSelect value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} aria-label="Filter by status">
                                    <option value="">Status</option>
                                    {statuses.map((status) => (
                                        <option key={status} value={status}>
                                            {status}
                                        </option>
                                    ))}
                                </EditSelect>
                            </div>
                        </HeaderRow>
                        <TableScrollContainer>
                            {loading && <LoadingSpinner text="Loading change sets..." />}
                            {!loading && !error && paginatedEntries.length === 0 && <NoData>No change sets found.</NoData>}
                            {!loading &&
                                paginatedEntries.length > 0 &&
                                paginatedEntries.map((entry) => (
                                    <ChangeRow
                                        key={entry.id}
                                        clickable
                                        onClick={() => handleRowClick(entry.id)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === 'Enter' && handleRowClick(entry.id)}
                                    >
                                        <div className="changeType">{entry.typeName}</div>
                                        <div className="dateTime">{formatDateTime(entry.date)}</div>
                                        <div className="category">{entry.categoryName}</div>
                                        <HideOverflow className="itemChanged">{entry.displayInformation}</HideOverflow>
                                        <HideOverflow className="changedBy">{entry.userName}</HideOverflow>
                                        <div className="changeStatus">
                                            <StatusBadge status={entry.statusName.toLowerCase() as 'approved' | 'pending' | 'rejected'}>
                                                {entry.statusName}
                                            </StatusBadge>
                                        </div>
                                    </ChangeRow>
                                ))}
                        </TableScrollContainer>
                    </BorderedContainer>

                    {/* Pagination */}
                    <Pagination>
                        <ul>
                            <li className={currentPage === 1 ? 'disabled' : ''}>
                                <PaginationButton onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                                    <FontAwesomeIcon icon={faChevronLeft} /> Prev
                                </PaginationButton>
                            </li>
                            {getPageNumbers().map((pageNumber) => (
                                <li key={pageNumber} className={pageNumber === currentPage ? 'active' : ''}>
                                    <PaginationButton onClick={() => handlePageChange(pageNumber)}>{pageNumber}</PaginationButton>
                                </li>
                            ))}
                            <li className={currentPage === totalPages ? 'disabled' : ''}>
                                <PaginationButton onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                                    Next <FontAwesomeIcon icon={faChevronRight} />
                                </PaginationButton>
                            </li>
                        </ul>
                    </Pagination>
                </MainContent>
            </ChangelogPage>
        </PageWrapper>
    );
};

export default ChangeLogPage;
